import { apiClient, API_PRX } from '@indipia/shared';
import type { ApiResponse } from '@indipia/shared-types';
import type {
  Agent,
  AddressSearchItem,
  BuildingItem,
  CoordResult,
  CreateSitePayload,
  CustomerExistsResult,
  FloorPlanTypeItem,
  FloorPlanThumbnailItem,
  SiteDetail,
  SiteListItem,
  ActiveWorklogItem,
  SchedulePeriod,
  WorklogCreateRequest,
  WorklogCreateResult,
} from './types';

export type { CreateSitePayload };

// X-Comp-Id / X-User-Id 는 FlowGate가 JWT에서 추출해 주입한다 (C-05 §3.5). FE 직접 세팅 금지.

// ── 에이전트 ────────────────────────────────────────────────────────────────

/** GET /indipia/agents — 활성 에이전트 목록 (requiredSteps 조회 목적) */
export async function fetchAgents(): Promise<Agent[]> {
  const res = await apiClient.get<ApiResponse<Agent[]>>(`${API_PRX.INDIPIA}/agents`);
  return Array.isArray(res.data.data) ? res.data.data : [];
}

// ── 고객 (Step A) ────────────────────────────────────────────────────────────

/**
 * GET /customer/customers?customerMobile=... — 연락처 존재 확인
 * customerMobile 파라미터는 하이픈 없는 순수 숫자 (예: "01012345678")
 */
export async function checkCustomerByMobile(customerMobile: string): Promise<CustomerExistsResult> {
  const res = await apiClient.get<ApiResponse<CustomerExistsResult>>(
    `${API_PRX.CUSTOMER}/customers`,
    { params: { customerMobile: customerMobile.replace(/-/g, '') } },
  );
  return res.data.data;
}

/**
 * POST /customer/customers — 새 고객 등록
 * customerMobile은 하이픈 없는 순수 숫자로 전송
 */
export async function createCustomer(payload: {
  customerName: string;
  customerMobile: string;
  customerAddress?: string;
  addressDtl?: string;
  sigunguCode?: string;
}): Promise<{ customerId: number }> {
  const res = await apiClient.post<ApiResponse<{ customerId: number; customerName: string }>>(
    `${API_PRX.CUSTOMER}/customers`,
    { ...payload, customerMobile: payload.customerMobile.replace(/-/g, '') },
  );
  return { customerId: res.data.data.customerId };
}

// ── 주소 검색 (Step B) ───────────────────────────────────────────────────────

/** GET /indipia/address/search?q=... — 도로명주소 검색 (2자 이상) */
export async function searchAddress(q: string): Promise<AddressSearchItem[]> {
  const res = await apiClient.get<ApiResponse<AddressSearchItem[]>>(
    `${API_PRX.INDIPIA}/address/search`,
    { params: { q } },
  );
  return Array.isArray(res.data.data) ? res.data.data : [];
}

/**
 * POST /indipia/address/coord — 주소 구성코드 → WGS84 좌표 변환
 * address/search 결과 항목의 필드를 그대로 전달
 */
export async function fetchCoord(params: {
  admCd: string;
  rnMgtSn: string;
  udrtYn: string;
  buldMnnm: number;
  buldSlno: number;
}): Promise<CoordResult> {
  const res = await apiClient.post<ApiResponse<CoordResult>>(
    `${API_PRX.INDIPIA}/address/coord`,
    params,
  );
  return res.data.data;
}

/**
 * POST /indipia/buildings — 신규 건물 마스터 등록
 * sizeLabel 매핑 없는 직접 입력 면적을 건물 마스터로 저장. 응답은 raw bldgId(int).
 */
export async function createBuilding(payload: {
  bldgName?: string;
  roadAddress?: string;
  bldgPyeong?: number;
  squareMeters?: number;
}): Promise<{ bldgId: number }> {
  const res = await apiClient.post<ApiResponse<number>>(`${API_PRX.INDIPIA}/buildings`, payload);
  return { bldgId: res.data.data };
}

/** GET /indipia/buildings?roadAddress=... — 도로명주소 기준 건물 평형 목록 */
export async function fetchBuildings(roadAddress: string): Promise<BuildingItem[]> {
  const res = await apiClient.get<ApiResponse<BuildingItem[]>>(
    `${API_PRX.INDIPIA}/buildings`,
    { params: { roadAddress } },
  );
  return Array.isArray(res.data.data) ? res.data.data : [];
}

// ── 현장 (Step B) ────────────────────────────────────────────────────────────

/**
 * GET /customer/sites?customerId= — 고객의 현장 목록 (Step A 기존 고객 감지 시)
 * site_status_not=DONE — 완료 현장은 서버(Indipia LIMIT 적용 이전)에서 미리 제외.
 * 응답을 받은 뒤 클라이언트에서 걸러내면 활동로그 상위 20건 중 다수가 DONE일 때
 * 실제 노출 건수가 20건보다 크게 줄어들 수 있어(홈 화면 fetchRecentSites와 동일 패턴).
 */
export async function fetchSitesByCustomer(customerId: number): Promise<SiteListItem[]> {
  const res = await apiClient.get<ApiResponse<{ items: SiteListItem[] }>>(
    `${API_PRX.CUSTOMER}/sites`,
    { params: { customerId, limit: 20, sort: 'last_access', site_status_not: 'DONE' } },
  );
  return res.data.data.items ?? [];
}

/** POST /customer/sites — 현장 등록 */
export async function createSite(payload: CreateSitePayload): Promise<{ siteId: number }> {
  const res = await apiClient.post<ApiResponse<{ siteId: number }>>(
    `${API_PRX.CUSTOMER}/sites`,
    payload,
  );
  return { siteId: res.data.data.siteId };
}

/** GET /customer/sites/{siteId} — 현장 상세 조회 */
export async function fetchSite(siteId: number): Promise<SiteDetail> {
  const res = await apiClient.get<ApiResponse<SiteDetail>>(
    `${API_PRX.CUSTOMER}/sites/${siteId}`,
  );
  return res.data.data;
}

/** PATCH /customer/sites/{siteId} — 현장 정보 수정 (floorPlanId 등 일반 필드) */
export async function updateSite(
  siteId: number,
  payload: Partial<{ floorPlanId: number; sitePyeong: number; squareMeters: number; bldgId: number }>,
): Promise<void> {
  await apiClient.patch(`${API_PRX.CUSTOMER}/sites/${siteId}`, payload);
}

/** PATCH /customer/sites/{siteId}/size — 면적 정보만 수정 (bldgId 연결 포함) */
export async function updateSiteSize(
  siteId: number,
  payload: { bldgId?: number; sitePyeong?: number; squareMeters?: number },
): Promise<void> {
  await apiClient.patch(`${API_PRX.CUSTOMER}/sites/${siteId}/size`, payload);
}

/** PATCH /customer/sites/{siteId}/access — 최근 접근 시각 갱신 */
export async function updateSiteAccess(siteId: number): Promise<void> {
  await apiClient.patch(`${API_PRX.CUSTOMER}/sites/${siteId}/access`);
}

// ── 도면 (Step C) ─────────────────────────────────────────────────────────────

/** GET /floorplan/indipia/plans/building/{bldgId} — 건물별 도면 타입 목록 (useYn 포함) */
export async function fetchFloorPlanTypes(bldgId: number): Promise<FloorPlanTypeItem[]> {
  const res = await apiClient.get<ApiResponse<FloorPlanTypeItem[]>>(
    `/floorplan/indipia/plans/building/${bldgId}`,
  );
  return Array.isArray(res.data.data) ? res.data.data : [];
}

/** GET /floorplan/indipia/plans/building/{bldgId}/thumbnails — 썸네일 + sizeType 목록 */
export async function fetchFloorPlanThumbnails(bldgId: number): Promise<FloorPlanThumbnailItem[]> {
  const res = await apiClient.get<ApiResponse<FloorPlanThumbnailItem[]>>(
    `/floorplan/indipia/plans/building/${bldgId}/thumbnails`,
  );
  return Array.isArray(res.data.data) ? res.data.data : [];
}

/**
 * GET /indipia/image/{filename} — 썸네일 이미지 blob 조회
 * thumbnailUrl 전체에서 파일명만 추출해 API 클라이언트 경유 호출 (nginx 직접 접근 방지)
 * 반환값: ObjectURL 문자열 (사용 후 URL.revokeObjectURL 필요)
 */
export async function fetchThumbnailObjectUrl(thumbnailUrl: string): Promise<string> {
  const filename = thumbnailUrl.split('/').pop() ?? '';
  const res = await apiClient.get<Blob>(
    `${API_PRX.INDIPIA}/image/${filename}`,
    { responseType: 'blob' },
  );
  return URL.createObjectURL(res.data);
}

/**
 * POST /floorplan/indipia/conversions/vulcan — 이미지 → EditorJSON 변환 (DB 저장 없음)
 * Content-Type: multipart/form-data
 */
export async function convertFloorPlan(file: File): Promise<object> {
  const form = new FormData();
  form.append('image', file);
  const res = await apiClient.post<ApiResponse<object>>(
    '/floorplan/indipia/conversions/vulcan',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data.data;
}

/**
 * POST /floorplan/indipia/plans — 도면 신규 저장
 * vulcan 변환 결과(planJson) + siteId/bldgId/compId 를 함께 전달
 */
export async function saveFloorPlan(payload: {
  bldgId: number;
  siteId: number;
  compId?: number;
  planJson: object;
  registeredByType?: string;
}): Promise<{ planId: number }> {
  const res = await apiClient.post<ApiResponse<{ planId: number }>>(
    '/floorplan/indipia/plans',
    { registeredByType: 'USER', ...payload },
  );
  return { planId: res.data.data.planId };
}

// ── 공사기간 (Step D) ─────────────────────────────────────────────────────────

/**
 * GET /schedule/schedules?siteIds= — 현장에 등록된 공사스케줄의 기간 조회
 * 파라미터명은 siteIds(복수) — 단수 siteId로 보내면 서버가 인식 못 해 회사 전체가 반환된다.
 * Step D 날짜 입력의 기본값을 채우는 용도. 스케줄이 없거나 조회 실패면 null 로 degrade
 * (기본값이 없을 뿐 등록 흐름은 계속 진행된다).
 */
export async function fetchSiteSchedulePeriod(siteId: number): Promise<SchedulePeriod | null> {
  try {
    const res = await apiClient.get<
      ApiResponse<{ schedules: { scheduleId: number; siteId: number; startDate: string | null; endDate: string | null }[] }>
    >('/schedule/schedules', { params: { siteIds: siteId } });
    const found = (res.data.data?.schedules ?? []).find(
      (s) => s.siteId === siteId && s.startDate && s.endDate,
    );
    return found
      ? { scheduleId: found.scheduleId, startDate: found.startDate!, endDate: found.endDate! }
      : null;
  } catch {
    return null;
  }
}

/**
 * GET /worklog/worklogs?siteId= — 해당 현장에 <b>아직 완료되지 않은</b> 공사현황이 있는지 확인
 * 같은 현장에 중복 등록하면 서버가 거부하므로 Step D 진입 시 먼저 확인한다.
 * 완료(DONE)는 재등록 대상이라 제외. 조회 실패는 null 로 degrade(중복이면 저장 시점에 서버가 막는다).
 */
export async function fetchActiveWorklog(siteId: number): Promise<ActiveWorklogItem | null> {
  try {
    const res = await apiClient.get<ApiResponse<{ items: ActiveWorklogItem[] }>>(
      `${API_PRX.WORKLOG}/worklogs`,
      { params: { siteId, size: 20 } },
    );
    const items = res.data.data?.items ?? [];
    return items.find((w) => w.siteId === siteId && w.progressStatus !== 'DONE') ?? null;
  } catch {
    return null;
  }
}

// ── 공휴일 (Step D 공사일수 계산) ─────────────────────────────────────────────

/**
 * GET /schedule/holidays?from&to — 기간 내 공휴일 날짜(YYYY-MM-DD) 목록.
 * 총 공사일수(일요일·공휴일 제외) 계산용. 실패 시 빈 배열로 degrade(일요일만 제외).
 */
export async function fetchHolidays(from: string, to: string): Promise<string[]> {
  try {
    const res = await apiClient.get<ApiResponse<{ date: string }[]>>('/schedule/holidays', {
      params: { from, to },
    });
    return (res.data.data ?? []).map((h) => h.date);
  } catch {
    return [];
  }
}

/** POST /worklog/worklogs — 공사현황 등록 (진행상태는 공사시작일 기준 서버 자동결정) */
export async function createWorklog(payload: WorklogCreateRequest): Promise<WorklogCreateResult> {
  const res = await apiClient.post<ApiResponse<WorklogCreateResult>>(
    `${API_PRX.WORKLOG}/worklogs`,
    payload,
  );
  return res.data.data;
}
